/**
 * GlucoseCorrelationChart.jsx
 * Interactive chart showing blood glucose over time with meal and training markers
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea, Scatter, ComposedChart, Area
} from 'recharts';
import {
  Droplet, Utensils, Dumbbell, RefreshCw, Calendar, TrendingUp,
  TrendingDown, AlertTriangle, CheckCircle, Clock, Info,
  ChevronLeft, ChevronRight, CalendarDays, List
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GlucoseCorrelationChart = ({ customerId, customerName, hours = 48 }) => {
  const [loading, setLoading] = useState(true);
  const [glucoseData, setGlucoseData] = useState(null);
  const [mealCorrelations, setMealCorrelations] = useState([]);
  const [trainingCorrelations, setTrainingCorrelations] = useState([]);
  const [timeRange, setTimeRange] = useState(24); // hours to display in window
  const [activeTab, setActiveTab] = useState('chart');
  const [selectedDate, setSelectedDate] = useState(null); // For daily view
  const [selectedChartDate, setSelectedChartDate] = useState(null); // For curve view date navigation
  const [viewMode, setViewMode] = useState('range'); // 'range' or 'daily'
  const [windowStart, setWindowStart] = useState(0); // hours offset from start of day (0-24)

  useEffect(() => {
    if (customerId) {
      loadAllData();
    }
  }, [customerId, selectedChartDate]);

  // Reset window when time range changes
  useEffect(() => {
    setWindowStart(0);
  }, [timeRange, selectedChartDate]);

  const loadAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    // Calculate date range based on selectedChartDate
    let dateParam = '';
    if (selectedChartDate) {
      dateParam = `&date=${selectedChartDate}`;
    }
    
    try {
      const [glucoseRes, mealRes, trainingRes] = await Promise.allSettled([
        axios.get(
          `${API}/glucose/admin/customer/${customerId}?hours=${timeRange}${dateParam}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${API}/glucose/meal-correlation?customer_id=${customerId}&days=14`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${API}/glucose/training-correlation?customer_id=${customerId}&days=14`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);

      if (glucoseRes.status === 'fulfilled') {
        setGlucoseData(glucoseRes.value.data);
      }
      if (mealRes.status === 'fulfilled') {
        setMealCorrelations(mealRes.value.data.correlations || []);
      }
      if (trainingRes.status === 'fulfilled') {
        setTrainingCorrelations(trainingRes.value.data.correlations || []);
      }
    } catch (error) {
      console.error('Error loading correlation data:', error);
      toast.error('Fehler beim Laden der Korrelationsdaten');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data - combine glucose readings with meal/training events
  const prepareChartData = () => {
    if (!glucoseData?.readings) return [];

    // Sort readings by time
    const sortedReadings = [...glucoseData.readings].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    // Filter by time window if timeRange < 24
    let filteredReadings = sortedReadings;
    if (timeRange < 24 && sortedReadings.length > 0) {
      const chartDate = selectedChartDate || new Date().toISOString().split('T')[0];
      const dayStart = new Date(`${chartDate}T00:00:00`);
      const windowStartTime = new Date(dayStart.getTime() + windowStart * 60 * 60 * 1000);
      const windowEndTime = new Date(windowStartTime.getTime() + timeRange * 60 * 60 * 1000);
      
      filteredReadings = sortedReadings.filter(reading => {
        const readingTime = new Date(reading.timestamp);
        return readingTime >= windowStartTime && readingTime < windowEndTime;
      });
    }

    // Map to chart format
    return filteredReadings.map(reading => {
      const time = new Date(reading.timestamp);
      
      // Check if there's a meal near this time (within 30 min before/after)
      const nearbyMeal = mealCorrelations.find(meal => {
        const mealTime = new Date(meal.meal_time);
        const diffMin = (time - mealTime) / (1000 * 60); // minutes (positive = after meal)
        // Show meal correlation: reading is 0-120 min after meal start
        return diffMin >= -15 && diffMin <= 120;
      });

      // Check if there's training near this time (during or up to 60 min after)
      const nearbyTraining = trainingCorrelations.find(training => {
        const trainingTime = new Date(training.workout_time);
        const trainingDuration = training.duration_min || 30;
        const diffMin = (time - trainingTime) / (1000 * 60);
        // Show training correlation: during workout or up to 60 min after
        return diffMin >= -15 && diffMin <= (trainingDuration + 60);
      });

      return {
        time: time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
        fullTime: time.toLocaleString('de-DE'),
        timestamp: reading.timestamp,
        glucose: reading.value,
        meal: nearbyMeal ? nearbyMeal.meal_name : null,
        mealCarbs: nearbyMeal?.carbs_g,
        training: nearbyTraining ? nearbyTraining.workout_type : null,
        trainingDuration: nearbyTraining?.duration_min,
        inRange: reading.value >= 70 && reading.value <= 140
      };
    });
  };

  const chartData = prepareChartData();

  // Get unique dates from glucose readings for daily navigation
  const getUniqueDates = () => {
    if (!glucoseData?.readings) return [];
    const dates = new Set();
    glucoseData.readings.forEach(reading => {
      const date = new Date(reading.timestamp).toISOString().split('T')[0];
      dates.add(date);
    });
    return Array.from(dates).sort().reverse();
  };

  const uniqueDates = getUniqueDates();

  // Prepare daily timeline data - grouped by events
  const prepareDailyTimeline = (date) => {
    if (!glucoseData?.readings) return [];

    const events = [];
    const targetDate = date || (uniqueDates[0] || new Date().toISOString().split('T')[0]);

    // Get all readings for this date
    const dayReadings = glucoseData.readings.filter(reading => {
      return new Date(reading.timestamp).toISOString().split('T')[0] === targetDate;
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Get meals for this date
    const dayMeals = mealCorrelations.filter(meal => {
      return new Date(meal.meal_time).toISOString().split('T')[0] === targetDate;
    });

    // Get training for this date
    const dayTraining = trainingCorrelations.filter(training => {
      return new Date(training.workout_time).toISOString().split('T')[0] === targetDate;
    });

    // Create timeline events
    dayReadings.forEach(reading => {
      const time = new Date(reading.timestamp);
      const timeStr = time.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

      // Find associated meal (within 30 min before)
      const associatedMeal = dayMeals.find(meal => {
        const mealTime = new Date(meal.meal_time);
        const diff = (time - mealTime) / (1000 * 60); // minutes
        return diff >= 0 && diff <= 120; // Reading is 0-120 min after meal
      });

      // Find associated training (within 60 min before/after)
      const associatedTraining = dayTraining.find(training => {
        const trainingTime = new Date(training.workout_time);
        const diff = Math.abs(time - trainingTime) / (1000 * 60);
        return diff <= 60;
      });

      events.push({
        time: timeStr,
        timestamp: reading.timestamp,
        glucose: reading.value,
        inRange: reading.value >= 70 && reading.value <= 140,
        meal: associatedMeal,
        training: associatedTraining
      });
    });

    return events;
  };

  // Slide window left/right
  const slideWindow = (direction) => {
    const step = Math.max(1, Math.floor(timeRange / 4)); // Slide by 1/4 of the window
    if (direction === 'left') {
      setWindowStart(Math.max(0, windowStart - step));
    } else {
      setWindowStart(Math.min(24 - timeRange, windowStart + step));
    }
  };

  // Calculate current time window for display
  const getTimeWindowLabel = () => {
    const startHour = windowStart;
    const endHour = Math.min(24, windowStart + timeRange);
    return `${String(startHour).padStart(2, '0')}:00 - ${String(endHour).padStart(2, '0')}:00`;
  };

  // Navigate between dates
  const navigateDate = (direction) => {
    const currentIndex = uniqueDates.indexOf(selectedDate || uniqueDates[0]);
    const newIndex = direction === 'prev' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex >= 0 && newIndex < uniqueDates.length) {
      setSelectedDate(uniqueDates[newIndex]);
    }
  };

  // Navigate chart dates
  const navigateChartDate = (direction) => {
    const currentChartDate = selectedChartDate || new Date().toISOString().split('T')[0];
    const current = new Date(currentChartDate);
    if (direction === 'prev') {
      current.setDate(current.getDate() - 1);
    } else {
      current.setDate(current.getDate() + 1);
    }
    // Don't go into the future
    if (current <= new Date()) {
      setSelectedChartDate(current.toISOString().split('T')[0]);
    }
  };

  const currentChartDate = selectedChartDate || new Date().toISOString().split('T')[0];

  const currentDate = selectedDate || uniqueDates[0];
  const dailyTimeline = prepareDailyTimeline(currentDate);

  // Custom tooltip - now shows causes directly
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    
    const data = payload[0].payload;
    
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 min-w-[200px]">
        <p className="font-medium text-gray-900 text-sm">{data.fullTime}</p>
        
        {/* Glucose Value */}
        <div className="flex items-center gap-2 mt-2">
          <Droplet className="w-4 h-4 text-red-500" />
          <span className={`text-lg font-bold ${
            data.glucose >= 70 && data.glucose <= 140 
              ? 'text-green-600' 
              : data.glucose < 70 
                ? 'text-red-600' 
                : 'text-orange-600'
          }`}>
            {data.glucose} mg/dL
          </span>
          {data.inRange ? (
            <CheckCircle className="w-4 h-4 text-green-500" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-orange-500" />
          )}
        </div>

        {/* Show cause directly - Meal */}
        {data.meal && (
          <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2 text-orange-700 font-medium">
              <Utensils className="w-4 h-4" />
              <span>{data.meal}</span>
            </div>
            {data.mealCarbs && (
              <p className="text-xs text-orange-600 mt-1 ml-6">{data.mealCarbs}g Kohlenhydrate</p>
            )}
          </div>
        )}

        {/* Show cause directly - Training */}
        {data.training && (
          <div className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 text-blue-700 font-medium">
              <Dumbbell className="w-4 h-4" />
              <span>{data.training}</span>
            </div>
            {data.trainingDuration && (
              <p className="text-xs text-blue-600 mt-1 ml-6">{data.trainingDuration} Minuten</p>
            )}
          </div>
        )}

        {/* No event */}
        {!data.meal && !data.training && (
          <p className="text-xs text-gray-500 mt-2">Keine Mahlzeit/Training zu diesem Zeitpunkt</p>
        )}
      </div>
    );
  };

  // Calculate meal markers for reference lines
  const getMealMarkers = () => {
    return mealCorrelations.map(meal => ({
      time: new Date(meal.meal_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      name: meal.meal_name,
      carbs: meal.carbs_g,
      spike: meal.glucose_spike
    }));
  };

  // Calculate training markers
  const getTrainingMarkers = () => {
    return trainingCorrelations.map(training => ({
      time: new Date(training.workout_time).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
      name: training.workout_type,
      duration: training.duration_min,
      drop: training.glucose_drop
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!glucoseData?.enabled || !glucoseData?.shared) {
    return (
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="p-8 text-center">
          <Droplet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Blutzucker-Daten</h3>
          <p className="text-gray-500">
            {customerName} hat die Blutzucker-Daten nicht aktiviert oder freigegeben.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Droplet className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold text-gray-900">Blutzucker-Analyse</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            <Button 
              variant={viewMode === 'range' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('range')}
              className="h-7 px-3"
            >
              <TrendingUp className="w-4 h-4 mr-1" />
              Kurve
            </Button>
            <Button 
              variant={viewMode === 'daily' ? 'default' : 'ghost'} 
              size="sm"
              onClick={() => setViewMode('daily')}
              className="h-7 px-3"
            >
              <CalendarDays className="w-4 h-4 mr-1" />
              Tagesansicht
            </Button>
          </div>

          {viewMode === 'range' && (
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value={6}>6 Stunden</option>
              <option value={12}>12 Stunden</option>
              <option value={24}>24 Stunden</option>
              <option value={48}>48 Stunden</option>
              <option value={72}>3 Tage</option>
              <option value={168}>7 Tage</option>
              <option value={720}>30 Tage</option>
            </select>
          )}

          <Button variant="outline" size="sm" onClick={loadAllData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Chart Date Navigation - Show in curve mode */}
      {viewMode === 'range' && (
        <div className="flex items-center justify-center gap-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigateChartDate('prev')}
            className="text-blue-700 hover:bg-blue-100"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="ml-1">Früher</span>
          </Button>
          <div className="flex items-center gap-2 px-4">
            <CalendarDays className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-900 text-lg">
              {new Date(currentChartDate).toLocaleDateString('de-DE', {
                weekday: 'short',
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigateChartDate('next')}
            disabled={currentChartDate >= new Date().toISOString().split('T')[0]}
            className="text-blue-700 hover:bg-blue-100"
          >
            <span className="mr-1">Später</span>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      )}

      {/* Time Window Slider - Show when not 24h */}
      {viewMode === 'range' && timeRange < 24 && (
        <div className="flex items-center justify-center gap-4 py-2 bg-green-50 rounded-lg border border-green-200">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => slideWindow('left')}
            disabled={windowStart <= 0}
            className="text-green-700 hover:bg-green-100"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="ml-1">Früher</span>
          </Button>
          <div className="flex items-center gap-2 px-4">
            <Clock className="w-5 h-5 text-green-600" />
            <span className="font-semibold text-green-900">
              {getTimeWindowLabel()}
            </span>
            <span className="text-green-600 text-sm">({timeRange}h Fenster)</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => slideWindow('right')}
            disabled={windowStart >= 24 - timeRange}
            className="text-green-700 hover:bg-green-100"
          >
            <span className="mr-1">Später</span>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      )}

      {/* Daily Navigation - Only show in daily mode */}
      {viewMode === 'daily' && uniqueDates.length > 0 && (
        <div className="flex items-center justify-center gap-4 py-2 bg-gray-50 rounded-lg">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigateDate('prev')}
            disabled={uniqueDates.indexOf(currentDate) >= uniqueDates.length - 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-gray-500" />
            <span className="font-semibold text-gray-900">
              {currentDate ? new Date(currentDate).toLocaleDateString('de-DE', {
                weekday: 'long',
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              }) : 'Kein Datum'}
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigateDate('next')}
            disabled={uniqueDates.indexOf(currentDate) <= 0}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Stats Overview */}
      {glucoseData?.stats && (
        <div className="grid grid-cols-5 gap-3">
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-3">
              <p className="text-xs text-gray-500">Durchschnitt</p>
              <p className="text-xl font-bold text-gray-900">{glucoseData.stats.average} <span className="text-xs font-normal">mg/dL</span></p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3">
              <p className="text-xs text-green-600">Im Zielbereich</p>
              <p className="text-xl font-bold text-green-700">{glucoseData.stats.time_in_range}%</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3">
              <p className="text-xs text-blue-600">Minimum</p>
              <p className="text-xl font-bold text-blue-700">{glucoseData.stats.min} <span className="text-xs font-normal">mg/dL</span></p>
            </CardContent>
          </Card>
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="p-3">
              <p className="text-xs text-orange-600">Maximum</p>
              <p className="text-xl font-bold text-orange-700">{glucoseData.stats.max} <span className="text-xs font-normal">mg/dL</span></p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-3">
              <p className="text-xs text-purple-600">Messungen</p>
              <p className="text-xl font-bold text-purple-700">{glucoseData.readings?.length || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Timeline View */}
      {viewMode === 'daily' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <List className="w-4 h-4 text-red-500" />
              Tagesverlauf - {currentDate ? new Date(currentDate).toLocaleDateString('de-DE') : ''}
            </CardTitle>
            <CardDescription>
              Alle Blutzuckerwerte mit Mahlzeiten und Training
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dailyTimeline.length > 0 ? (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {dailyTimeline.map((event, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      event.meal || event.training 
                        ? 'bg-white border-gray-200 shadow-sm' 
                        : 'bg-gray-50 border-gray-100'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Time */}
                      <div className="text-sm font-mono text-gray-500 w-14 pt-1">
                        {event.time}
                      </div>
                      
                      {/* Glucose Value */}
                      <div className={`flex items-center gap-2 min-w-[100px] ${
                        event.inRange ? 'text-green-600' : event.glucose < 70 ? 'text-red-600' : 'text-orange-600'
                      }`}>
                        <Droplet className="w-4 h-4" />
                        <span className="text-lg font-bold">{event.glucose}</span>
                        <span className="text-xs">mg/dL</span>
                      </div>

                      {/* Events */}
                      <div className="flex-1 flex flex-wrap gap-2">
                        {event.meal && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                            <Utensils className="w-4 h-4 text-orange-600" />
                            <span className="text-sm font-medium text-orange-800">{event.meal.meal_name}</span>
                            {event.meal.carbs_g && (
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                {event.meal.carbs_g}g Carbs
                              </Badge>
                            )}
                            {event.meal.glucose_spike && (
                              <Badge className={`text-xs ${
                                event.meal.glucose_spike > 40 
                                  ? 'bg-red-100 text-red-700' 
                                  : event.meal.glucose_spike > 25 
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-green-100 text-green-700'
                              }`}>
                                +{event.meal.glucose_spike} mg/dL
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {event.training && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                            <Dumbbell className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-medium text-blue-800">{event.training.workout_type}</span>
                            {event.training.duration_min && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                {event.training.duration_min} min
                              </Badge>
                            )}
                            {event.training.glucose_drop && event.training.glucose_drop > 0 && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                -{event.training.glucose_drop} mg/dL
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Keine Daten für diesen Tag</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Chart View - Tabs */}
      {viewMode === 'range' && (
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="chart" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Kurve
          </TabsTrigger>
          <TabsTrigger value="meals" className="flex items-center gap-2">
            <Utensils className="w-4 h-4" />
            Mahlzeiten ({mealCorrelations.length})
          </TabsTrigger>
          <TabsTrigger value="training" className="flex items-center gap-2">
            <Dumbbell className="w-4 h-4" />
            Training ({trainingCorrelations.length})
          </TabsTrigger>
        </TabsList>

        {/* Chart Tab */}
        <TabsContent value="chart" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-red-500" />
                Blutzucker-Verlauf
              </CardTitle>
              <CardDescription>
                Glukosewerte mit Mahlzeiten (🍽️) und Training (💪) Markierungen
              </CardDescription>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      
                      {/* Target range area (70-140 mg/dL) */}
                      <ReferenceArea y1={70} y2={140} fill="#22c55e" fillOpacity={0.1} />
                      
                      {/* Reference lines for targets */}
                      <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="3 3" label={{ value: '70', position: 'left', fontSize: 10 }} />
                      <ReferenceLine y={140} stroke="#22c55e" strokeDasharray="3 3" label={{ value: '140', position: 'left', fontSize: 10 }} />
                      <ReferenceLine y={180} stroke="#f97316" strokeDasharray="3 3" label={{ value: '180 (Hoch)', position: 'left', fontSize: 10 }} />
                      
                      <XAxis 
                        dataKey="time" 
                        tick={{ fontSize: 11 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        domain={[40, 220]} 
                        tick={{ fontSize: 11 }}
                        label={{ value: 'mg/dL', angle: -90, position: 'insideLeft', fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      
                      {/* Area fill for better visualization */}
                      <Area 
                        type="monotone" 
                        dataKey="glucose" 
                        fill="#ef4444" 
                        fillOpacity={0.1}
                        stroke="none"
                      />
                      
                      {/* Main glucose line */}
                      <Line
                        type="monotone"
                        dataKey="glucose"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={(props) => {
                          const { cx, cy, payload } = props;
                          // Show special dots for meals and training
                          if (payload.meal) {
                            return (
                              <circle cx={cx} cy={cy} r={6} fill="#f97316" stroke="#fff" strokeWidth={2} />
                            );
                          }
                          if (payload.training) {
                            return (
                              <circle cx={cx} cy={cy} r={6} fill="#3b82f6" stroke="#fff" strokeWidth={2} />
                            );
                          }
                          // Out of range dots
                          if (!payload.inRange) {
                            return (
                              <circle cx={cx} cy={cy} r={4} fill="#f97316" stroke="#fff" strokeWidth={1} />
                            );
                          }
                          return null;
                        }}
                        activeDot={{ r: 6, fill: '#ef4444', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <Droplet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Keine Glukose-Daten für diesen Zeitraum</p>
                  </div>
                </div>
              )}
              
              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500/20 border border-green-500"></div>
                  <span className="text-gray-600">Zielbereich (70-140)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="text-gray-600">Mahlzeit</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-600">Training</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Meals Tab */}
        <TabsContent value="meals" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Utensils className="w-4 h-4 text-orange-500" />
                Mahlzeiten-Korrelation
              </CardTitle>
              <CardDescription>
                Wie reagiert der Blutzucker auf verschiedene Mahlzeiten?
              </CardDescription>
            </CardHeader>
            <CardContent>
              {mealCorrelations.length > 0 ? (
                <div className="space-y-3">
                  {/* Table Header */}
                  <div className="grid grid-cols-7 gap-2 p-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-600">
                    <div>Zeit</div>
                    <div>Mahlzeit</div>
                    <div className="text-right">Carbs</div>
                    <div className="text-right">Vorher</div>
                    <div className="text-right">Peak</div>
                    <div className="text-right">Anstieg</div>
                    <div className="text-right">Zeit bis Peak</div>
                  </div>
                  
                  {mealCorrelations.map((meal, idx) => (
                    <div key={idx} className="grid grid-cols-7 gap-2 p-3 bg-white border rounded-lg items-center text-sm">
                      <div className="text-gray-600">
                        {new Date(meal.meal_time).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="font-medium text-gray-900 truncate">{meal.meal_name}</div>
                      <div className="text-right text-gray-600">
                        {meal.carbs_g ? `${meal.carbs_g}g` : '-'}
                      </div>
                      <div className="text-right">
                        <span className="text-blue-600 font-medium">{meal.glucose_before || '-'}</span>
                      </div>
                      <div className="text-right">
                        <span className={`font-medium ${
                          meal.glucose_peak && meal.glucose_peak > 140 ? 'text-orange-600' : 'text-gray-900'
                        }`}>
                          {meal.glucose_peak || '-'}
                        </span>
                      </div>
                      <div className="text-right">
                        {meal.glucose_spike !== null && meal.glucose_spike !== undefined ? (
                          <Badge className={`${
                            meal.glucose_spike > 40 
                              ? 'bg-red-100 text-red-700' 
                              : meal.glucose_spike > 25 
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-green-100 text-green-700'
                          }`}>
                            +{meal.glucose_spike}
                          </Badge>
                        ) : '-'}
                      </div>
                      <div className="text-right text-gray-600">
                        {meal.time_to_peak_min ? `${meal.time_to_peak_min} min` : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Keine Mahlzeiten-Daten für diesen Zeitraum</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Training Tab */}
        <TabsContent value="training" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Dumbbell className="w-4 h-4 text-blue-500" />
                Training-Korrelation
              </CardTitle>
              <CardDescription>
                Wie beeinflusst Training den Blutzucker?
              </CardDescription>
            </CardHeader>
            <CardContent>
              {trainingCorrelations.length > 0 ? (
                <div className="space-y-3">
                  {/* Table Header */}
                  <div className="grid grid-cols-6 gap-2 p-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-600">
                    <div>Zeit</div>
                    <div>Training</div>
                    <div className="text-right">Dauer</div>
                    <div className="text-right">Vorher</div>
                    <div className="text-right">Nachher</div>
                    <div className="text-right">Veränderung</div>
                  </div>
                  
                  {trainingCorrelations.map((training, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 p-3 bg-white border rounded-lg items-center text-sm">
                      <div className="text-gray-600">
                        {new Date(training.workout_time).toLocaleString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="font-medium text-gray-900">{training.workout_type}</div>
                      <div className="text-right text-gray-600">{training.duration_min} min</div>
                      <div className="text-right">
                        <span className="text-blue-600 font-medium">{training.glucose_before || '-'}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-900 font-medium">{training.glucose_after || '-'}</span>
                      </div>
                      <div className="text-right">
                        {training.glucose_drop !== null && training.glucose_drop !== undefined ? (
                          <Badge className={`${
                            training.glucose_drop > 0 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-orange-100 text-orange-700'
                          }`}>
                            {training.glucose_drop > 0 ? '-' : '+'}{Math.abs(training.glucose_drop)}
                          </Badge>
                        ) : '-'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Dumbbell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Keine Training-Daten für diesen Zeitraum</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Interpretation der Daten</p>
              <ul className="list-disc list-inside space-y-1 text-blue-800">
                <li><strong>Zielbereich:</strong> 70-140 mg/dL ist optimal</li>
                <li><strong>Nach Mahlzeiten:</strong> Ein Anstieg von &lt;30 mg/dL ist ideal</li>
                <li><strong>Nach Training:</strong> Moderater Abfall ist normal und gesund</li>
                <li><strong>Peak-Zeit:</strong> Glukose-Peak sollte 30-60 min nach dem Essen sein</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GlucoseCorrelationChart;
